import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import CooldownTimer from '../components/CooldownTimer';
import SafeImage from '../components/SafeImage';
import { useAuth } from '../context/AuthContext';
import { PLANS } from '../mock/mockData';
import api, { TOKEN_KEY } from '../lib/api';
import { resolveRankImageSrc } from '../utils/imageUtils';
import { Plus, Share2, Eye, Trash2, Gauge, Box, Crown, Search, Sparkles, Check, Lock, KeyRound, X, ShieldCheck } from 'lucide-react';

export default function Dashboard() {
  const { user, accounts, cooldowns, removeAccount, subscriptionNotice, markSubscriptionNoticeSeen } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [copiedId, setCopiedId] = useState('');
  const [selected, setSelected] = useState([]);
  const [controlLocked, setControlLocked] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', password: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [activationToast, setActivationToast] = useState(null);
  const shownActivationNotice = useRef('');
  const { bulkRemoveAccounts } = useAuth();

  const plan = PLANS.find((p) => p.id === user?.plan) || PLANS[0];
  const canUseControlPanel = ['immortal', 'radiant'].includes(plan.id);
  const filtered = accounts.filter((a) => ((a.ign || '') + (a.tag || '')).toLowerCase().includes(q.toLowerCase()));
  const usedSlots = accounts.length + cooldowns.length;

  useEffect(() => {
    if (!subscriptionNotice?.id || shownActivationNotice.current === subscriptionNotice.id) return;
    shownActivationNotice.current = subscriptionNotice.id;
    const planName = subscriptionNotice.plan
      ? subscriptionNotice.plan.charAt(0).toUpperCase() + subscriptionNotice.plan.slice(1)
      : '';
    setActivationToast({
      title: 'Subscription Activated',
      body: planName
        ? `Your ${planName} subscription has been successfully activated.`
        : 'Your subscription has been successfully activated.',
    });
    markSubscriptionNoticeSeen();
  }, [subscriptionNotice, markSubscriptionNoticeSeen]);

  useEffect(() => {
    if (!activationToast) return;
    const id = setTimeout(() => setActivationToast(null), 5500);
    return () => clearTimeout(id);
  }, [activationToast]);

    const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map(a => a.id));
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selected.length} accounts?`)) return;
    await bulkRemoveAccounts(selected);
    setSelected([]);
  };

  const copyShare = async (slug) => {
    const url = `${window.location.origin}/share/${slug}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedId(slug);
      setTimeout(() => setCopiedId(''), 1800);
    } catch (e) {
      window.prompt('Copy this share link:', url);
    }
  };

  const openControlPanel = () => {
    if (!canUseControlPanel) {
      setControlLocked(true);
      return;
    }
    navigate('/control-panel');
  };

  const openPasswordModal = () => {
    setPasswordForm({ current: '', password: '', confirm: '' });
    setPasswordMessage('');
    setPasswordError('');
    setPasswordModalOpen(true);
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    setPasswordError('');
    if (!passwordForm.current) {
      setPasswordError('Current password is required.');
      return;
    }
    if (passwordForm.password.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (passwordForm.password !== passwordForm.confirm) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordLoading(true);
    try {
      const { data } = await api.post('/auth/request-password-reset', {
        current_password: passwordForm.current,
        password: passwordForm.password,
      });
      if (data?.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
      }
      setPasswordForm({ current: '', password: '', confirm: '' });
      setPasswordMessage(data?.message || 'Password reset successfully.');
    } catch (err) {
      setPasswordError(err?.response?.data?.detail || 'Password reset failed. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Layout>
      {activationToast && (
        <div className="fixed top-20 right-4 z-[130] max-w-sm w-[calc(100%-2rem)] rounded border border-cyan-400/40 bg-zinc-950/95 shadow-[0_0_24px_rgba(34,211,238,0.18)] p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 shrink-0 rounded grid place-items-center border border-cyan-400/50 bg-cyan-400/10">
              <ShieldCheck className="w-4 h-4 text-cyan-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono-display text-sm text-white">{activationToast.title}</div>
              <p className="text-xs text-zinc-300 mt-1 leading-relaxed">{activationToast.body}</p>
            </div>
            <button onClick={() => setActivationToast(null)} className="text-zinc-500 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <section className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 mb-1">// operator dashboard</div>
            <h1 className="font-mono-display text-3xl text-white">Welcome back, <span className="neon-orange">{user?.username}</span></h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={openControlPanel}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded border text-xs uppercase tracking-widest transition-colors ${
                canUseControlPanel
                  ? 'border-red-500/60 text-red-200 bg-red-500/10 hover:border-red-300 hover:bg-red-500/15'
                  : 'border-red-900/60 text-red-300/70 bg-red-950/20 hover:border-red-500/60'
              }`}
            >
              <Crown className="w-3.5 h-3.5" />
              Control Panel
              {!canUseControlPanel && <Lock className="w-3 h-3" />}
            </button>
            <CooldownTimer cooldowns={cooldowns} />
            {plan.id !== 'radiant' && (
              <Link to="/pricing" className="btn-ghost"><Sparkles className="w-3.5 h-3.5"/>Upgrade</Link>
            )}
            <button onClick={openPasswordModal} className="btn-ghost"><KeyRound className="w-3.5 h-3.5"/>Reset Password</button>
            <button onClick={() => navigate('/dashboard/add')} className="btn-neon"><Plus className="w-4 h-4"/>Add Account</button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="neon-card rounded p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded grid place-items-center border border-orange-500/50 bg-orange-500/10"><Box className="w-5 h-5 text-orange-300"/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">Slots Used</div>
              <div className="font-mono-display text-2xl text-white tabular">{usedSlots} / {plan.slots}</div>
            </div>
          </div>
          <div className="neon-card rounded p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded grid place-items-center border border-cyan-400/50 bg-cyan-400/10"><Crown className="w-5 h-5 text-cyan-300"/></div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">Current Plan</div>
              <div className="font-mono-display text-2xl text-white">{plan.name}</div>
            </div>
            {plan.id !== 'radiant' && (
              <Link to="/pricing" className="text-[10px] uppercase tracking-widest text-orange-300 border border-orange-400/40 hover:border-orange-300 hover:bg-orange-500/10 px-2 py-1 rounded transition-colors">Upgrade</Link>
            )}
          </div>
          <div className="neon-card rounded p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded grid place-items-center border border-orange-500/50 bg-orange-500/10"><Gauge className="w-5 h-5 text-orange-300"/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">Active Cooldowns</div>
              <div className="font-mono-display text-2xl text-white tabular">{cooldowns.length}</div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 mb-5">
          <div className="flex-1 flex items-center gap-2 bg-black/40 border border-zinc-800 focus-within:border-orange-400/70 rounded px-3 py-2">
            <Search className="w-4 h-4 text-zinc-500"/>
            <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search accounts by IGN or tag..." className="bg-transparent flex-1 outline-none text-white placeholder:text-zinc-600"/>
          </div>
        </div>

                {/* Selection Bar */}
        {accounts.length > 0 && (
          <div className="flex items-center justify-between mb-4 bg-zinc-900/50 border border-zinc-800 rounded px-4 py-2">
            <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={selected.length === filtered.length && filtered.length > 0} 
                onChange={selectAll}
                className="w-4 h-4 rounded border-zinc-700 bg-black accent-orange-500"
              />
              <span className="text-xs uppercase tracking-widest text-zinc-400">
                {selected.length} Selected
              </span>
            </div>
            {selected.length > 0 && (
              <button 
                onClick={handleBulkDelete}
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete Selected
              </button>
            )}
          </div>
        )}

        {/* Accounts grid */}
        {filtered.length === 0 ? (
          <div className="neon-card rounded-lg p-10 text-center">
            <div className="font-mono-display text-xl text-white mb-2">Your vault is empty</div>
            <p className="text-sm text-zinc-400 mb-5">Add your first Valorant account using the Access URL method.</p>
            <Link to="/dashboard/add" className="btn-neon inline-flex"><Plus className="w-4 h-4"/>Add Account</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((a) => (
                            <div key={a.id} className={`neon-card rounded-lg overflow-hidden group relative ${selected.includes(a.id) ? 'ring-2 ring-orange-500/50' : ''}`}>
                <div className="absolute top-3 left-3 z-10">
                   <input 
                    type="checkbox" 
                    checked={selected.includes(a.id)} 
                    onChange={() => toggleSelect(a.id)}
                    className="w-4 h-4 rounded border-zinc-700 bg-black/60 accent-orange-500"
                   />
                </div>
                <div className="relative h-32">
                  <SafeImage source={a.banner} alt={a.ign} className="absolute inset-0 w-full h-full object-cover" fallbackClassName="absolute inset-0 w-full h-full" fallbackIcon={Box} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"/>
                  <div className="absolute top-3 right-3 px-2 py-0.5 text-[10px] uppercase tracking-widest rounded bg-black/60 border border-cyan-400/30 text-cyan-200">{a.region}</div>
                  <div className="absolute bottom-2 left-3 right-3">
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-300">{a.title}</div>
                        <div className="font-mono-display text-white text-lg leading-tight">{a.ign} <span className="text-orange-300">#{a.tag}</span></div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase text-zinc-400">Lvl</div>
                        <div className="font-mono-display neon-orange tabular text-lg">{a.level}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <SafeImage source={a.rank} resolver={resolveRankImageSrc} alt={a.rank?.name || 'Rank'} className="w-8 h-8 object-contain" fallbackClassName="w-8 h-8" fallbackIcon={Crown} />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400">Current Rank</div>
                      <div className="text-white font-mono-display">{a.rank.name}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center mb-4">
                    <div className="rounded border border-zinc-800 py-1.5"><div className="text-[10px] text-zinc-500 uppercase">VP</div><div className="font-mono-display text-orange-300 tabular text-sm">{a.totalValueVP.toLocaleString()}</div></div>
                    <div className="rounded border border-zinc-800 py-1.5"><div className="text-[10px] text-zinc-500 uppercase">Skins</div><div className="font-mono-display text-cyan-300 tabular text-sm">{a.skinsCount}</div></div>
                    <div className="rounded border border-zinc-800 py-1.5"><div className="text-[10px] text-zinc-500 uppercase">Agents</div><div className="font-mono-display text-cyan-300 tabular text-sm">{a.agentsCount}</div></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/account/${a.id}`} className="btn-ghost flex-1 justify-center"><Eye className="w-3.5 h-3.5"/>View</Link>
                    <button onClick={()=>copyShare(a.shareSlug)} className="btn-ghost flex-1 justify-center">
                      {copiedId === a.shareSlug ? <><Check className="w-3.5 h-3.5"/>Copied</> : <><Share2 className="w-3.5 h-3.5"/>Share</>}
                    </button>
                    <button onClick={()=>removeAccount(a.id)} className="p-2 rounded border border-red-500/40 hover:border-red-400 hover:bg-red-500/10 text-red-300 transition-colors" title="Remove">
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {controlLocked && (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setControlLocked(false)}>
          <div onClick={(e) => e.stopPropagation()} className="neon-card rounded-lg p-6 max-w-md w-full text-center border-red-900/70 bg-red-950/20">
            <Lock className="w-7 h-7 text-red-300 mx-auto mb-3" />
            <div className="font-mono-display text-xl text-white mb-2">Control Panel Locked</div>
            <p className="text-sm text-red-200/70 mb-5">Subscribe to Immortal or Radiant to unlock the Control Panel.</p>
            <div className="flex items-center justify-center gap-2">
              <Link to="/pricing" className="btn-neon">Upgrade Now</Link>
              <button onClick={() => setControlLocked(false)} className="btn-ghost">Close</button>
            </div>
          </div>
        </div>
      )}

      {passwordModalOpen && (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setPasswordModalOpen(false)}>
          <form onSubmit={resetPassword} onClick={(e) => e.stopPropagation()} className="neon-card rounded-lg p-6 max-w-md w-full border-cyan-900/60 bg-zinc-950/95">
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <div className="font-mono-display text-xl text-white flex items-center gap-2"><KeyRound className="w-5 h-5 text-cyan-300" />Reset Password</div>
                <p className="text-sm text-zinc-400 mt-1">Set a new password for your ValoRex account.</p>
              </div>
              <button type="button" onClick={() => setPasswordModalOpen(false)} className="p-1.5 rounded border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            {passwordError && (
              <div className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2">{passwordError}</div>
            )}
            {passwordMessage && (
              <div className="mb-4 text-sm text-cyan-300 bg-cyan-400/10 border border-cyan-400/30 rounded p-2">{passwordMessage}</div>
            )}
            <div className="space-y-4">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cyan-300">Current Password</span>
                <input
                  required
                  maxLength={128}
                  type="password"
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm((cur) => ({ ...cur, current: e.target.value }))}
                  className="mt-1 w-full bg-black/40 border border-zinc-800 focus:border-orange-400/70 rounded px-3 py-2 text-white outline-none"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cyan-300">New Password</span>
                <input
                  required
                  minLength={6}
                  maxLength={128}
                  type="password"
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm((cur) => ({ ...cur, password: e.target.value }))}
                  className="mt-1 w-full bg-black/40 border border-zinc-800 focus:border-orange-400/70 rounded px-3 py-2 text-white outline-none"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cyan-300">Confirm Password</span>
                <input
                  required
                  minLength={6}
                  maxLength={128}
                  type="password"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm((cur) => ({ ...cur, confirm: e.target.value }))}
                  className="mt-1 w-full bg-black/40 border border-zinc-800 focus:border-orange-400/70 rounded px-3 py-2 text-white outline-none"
                />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setPasswordModalOpen(false)} className="btn-ghost">Cancel</button>
              <button disabled={passwordLoading} className="btn-neon disabled:opacity-50 disabled:cursor-not-allowed">
                {passwordLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
